import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from '../i18n/useLanguage.js';
import { Button, type ButtonProps } from './Button.js';

export interface ConfirmButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  readonly label: string;
  readonly confirmLabel?: string;
  readonly onConfirm: () => void;
}

const CONFIRM_TIMEOUT_MS = 4000;

/**
 * Two-step destructive action: the first click arms it, the second performs it,
 * and it disarms itself after a few seconds.
 *
 * Cheaper than a modal for the many small deletions in this app, and it still
 * makes an accidental click harmless.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  ...rest
}: ConfirmButtonProps): ReactElement {
  const t = useTranslation();
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) {
      return;
    }
    const timer = setTimeout(() => setArmed(false), CONFIRM_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <Button
      {...rest}
      variant={armed ? 'danger' : (rest.variant ?? 'ghost')}
      onClick={() => {
        if (armed) {
          setArmed(false);
          onConfirm();
          return;
        }
        setArmed(true);
      }}
    >
      {armed ? (confirmLabel ?? t.common.sure) : label}
    </Button>
  );
}
